import { Badge, Box, Button, Divider, Group, Image, Popover, ScrollArea, SimpleGrid, Text, TextInput, UnstyledButton } from "@mantine/core"
import { useElementSize } from "@mantine/hooks"
import { CaretDownIcon, CheckIcon, MagnifyingGlassIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import classes from "./catalog.module.css"

export type BrandOption = { id: string; name: string; logo?: string | null; count: number }

/** Brand logo shown as is (no frame); the initial when there is no logo or it fails to load (external hosts). */
function BrandLogo({ brand }: Readonly<{ brand: BrandOption }>) {
  const [failed, setFailed] = React.useState(false)
  if (brand.logo && !failed) {
    return <Image src={brand.logo} alt="" fit="contain" w="100%" h="100%" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
  }
  return (
    <Text span className={classes.brandInitial} aria-hidden>
      {(brand.name.trim()[0] || "#").toUpperCase()}
    </Text>
  )
}

/** One brand as a logo tile with its name and count; the whole tile toggles the brand. */
function BrandTile({ brand, on, onToggle }: Readonly<{ brand: BrandOption; on: boolean; onToggle: () => void }>) {
  return (
    <UnstyledButton
      className={classes.brandTile}
      data-on={on || undefined}
      aria-pressed={on}
      title={`${brand.name} (${brand.count})`}
      onClick={onToggle}>
      <Box className={classes.brandLogo}>
        <BrandLogo brand={brand} />
        {on && (
          <Box component="span" className={classes.brandCheck} aria-hidden>
            <CheckIcon size={12} weight="bold" />
          </Box>
        )}
      </Box>
      <Text span className={classes.brandTileName}>
        {brand.name}
      </Text>
      <Text span className={classes.brandTileCount}>
        {brand.count}
      </Text>
    </UnstyledButton>
  )
}

const TILE_W = 92
const TILE_GAP = 12

/**
 * Logo row above the product grid (the earlier catalogue's brand row people liked): as many brands
 * as fit one row, chosen ones first, then most products; the last slot opens the full «Брэнд» list.
 * A tap filters at once; the dropdown keeps search and multi-select.
 */
export function BrandStrip({
  brands,
  selected,
  onToggle,
  onShowAll,
}: Readonly<{ brands: BrandOption[]; selected: string[]; onToggle: (id: string) => void; onShowAll: () => void }>) {
  const { t } = useTranslation("order")
  const { ref, width } = useElementSize()
  const sorted = React.useMemo(
    () =>
      [...brands].sort(
        (a, b) =>
          Number(selected.includes(b.id)) - Number(selected.includes(a.id)) || b.count - a.count || a.name.localeCompare(b.name),
      ),
    [brands, selected],
  )
  if (sorted.length < 2) return <Box ref={ref} />

  const fit = width > 0 ? Math.max(1, Math.floor((width + TILE_GAP) / (TILE_W + TILE_GAP))) : 0
  const showAll = sorted.length > fit
  const visible = sorted.slice(0, showAll ? Math.max(fit - 1, 0) : fit)
  const hidden = sorted.length - visible.length

  return (
    <Box ref={ref} className={classes.brandStrip}>
      {visible.map((b) => (
        <BrandTile key={b.id} brand={b} on={selected.includes(b.id)} onToggle={() => onToggle(b.id)} />
      ))}
      {showAll && (
        <UnstyledButton className={classes.brandTile} onClick={onShowAll} title={t("catalog.brandAll")}>
          <Box className={classes.brandLogo} data-more>
            <Text span className={classes.brandInitial}>{`+${hidden}`}</Text>
          </Box>
          <Text span className={classes.brandTileName}>
            {t("catalog.brandAll")}
          </Text>
          <Text span className={classes.brandTileCount}>
            {sorted.length}
          </Text>
        </UnstyledButton>
      )}
    </Box>
  )
}

type Props = {
  brands: BrandOption[]
  selected: string[]
  onApply: (ids: string[]) => void
  /** How many products the given brand selection would show. */
  countFor: (ids: string[]) => number
  /** Controlled open state, so the logo row's «Бүх брэнд» can open the same dropdown. */
  opened?: boolean
  onOpenedChange?: (opened: boolean) => void
}

const POPULAR_COUNT = 5

/**
 * Brand filter as a dropdown (canvas OrdB_CatalogFilters), replacing the
 * 58-logo strip (B16): search, the most-stocked brands as chips, then an A-Z
 * checklist. The choice applies on the footer button.
 */
export function BrandFilter({ brands, selected, onApply, countFor, opened: openedProp, onOpenedChange }: Props) {
  const { t } = useTranslation("order")
  const [openedState, setOpenedState] = React.useState(false)
  const opened = openedProp ?? openedState
  const setOpened = React.useCallback(
    (next: boolean | ((o: boolean) => boolean)) => {
      const value = typeof next === "function" ? next(opened) : next
      setOpenedState(value)
      onOpenedChange?.(value)
    },
    [opened, onOpenedChange],
  )
  const [draft, setDraft] = React.useState<string[]>(selected)
  const [query, setQuery] = React.useState("")

  React.useEffect(() => {
    if (opened) {
      setDraft(selected)
      setQuery("")
    }
  }, [opened, selected])

  const toggle = (id: string) => setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]))

  const popular = React.useMemo(() => [...brands].sort((a, b) => b.count - a.count).slice(0, POPULAR_COUNT), [brands])

  const groups = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = [...brands]
      .filter((b) => !q || b.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name))
    const map = new Map<string, BrandOption[]>()
    list.forEach((b) => {
      const letter = (b.name.trim()[0] || "#").toUpperCase()
      map.set(letter, [...(map.get(letter) || []), b])
    })
    return Array.from(map.entries())
  }, [brands, query])

  const draftCount = React.useMemo(() => countFor(draft), [countFor, draft])

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" shadow="md" radius="lg" trapFocus>
      <Popover.Target>
        <Button
          variant="default"
          radius="md"
          h={42}
          rightSection={<CaretDownIcon size={14} />}
          onClick={() => setOpened((o) => !o)}
          aria-expanded={opened}
        >
          <Group gap={6} wrap="nowrap">
            {t("catalog.brand")}
            {selected.length > 0 && (
              <Badge color="dark" size="md" circle>
                {selected.length}
              </Badge>
            )}
          </Group>
        </Button>
      </Popover.Target>
      <Popover.Dropdown className={classes.brandDropdown}>
        <Box p="md" pb="sm">
          <Group justify="space-between" mb="sm">
            <Text fz="var(--nw-fz-lead)" fw={650}>
              {t("catalog.brand")}
              <Text span inherit fw={400} c="var(--nw-text-2)">
                {` · ${brands.length}`}
              </Text>
            </Text>
            <Text className={classes.meta}>
              {draft.length > 0 ? t("catalog.brandSelected", { count: draft.length }) : t("catalog.brandMulti")}
            </Text>
          </Group>
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder={t("catalog.brandSearch")}
            aria-label={t("catalog.brandSearch")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            classNames={{ input: classes.brandSearch }}
            data-autofocus
          />
          {!query && popular.length > 0 && (
            <>
              <Text className={classes.brandGroupLabel} mt="md" mb={8}>
                {t("catalog.brandPopular")}
              </Text>
              <Group gap="sm" wrap="nowrap">
                {popular.map((b) => (
                  <BrandTile key={b.id} brand={b} on={draft.includes(b.id)} onToggle={() => toggle(b.id)} />
                ))}
              </Group>
            </>
          )}
        </Box>
        <Divider />
        <ScrollArea.Autosize mah={320} type="auto">
          <Box px="md" py="sm">
            {groups.length === 0 && <Text className={classes.meta}>{t("catalog.brandEmpty")}</Text>}
            {groups.map(([letter, list]) => (
              <Box key={letter} mb="sm">
                <Text className={classes.brandGroupLabel} mb={4}>
                  {letter}
                </Text>
                <SimpleGrid cols={{ base: 3, xs: 5 }} spacing="sm" verticalSpacing="sm">
                  {list.map((b) => (
                    <BrandTile key={b.id} brand={b} on={draft.includes(b.id)} onToggle={() => toggle(b.id)} />
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </Box>
        </ScrollArea.Autosize>
        <Divider />
        <Group justify="space-between" p="md">
          <UnstyledButton className={classes.linkButton} onClick={() => setDraft([])} disabled={draft.length === 0}>
            {t("catalog.brandClear")}
          </UnstyledButton>
          <Button
            color="dark"
            radius="md"
            onClick={() => {
              onApply(draft)
              setOpened(false)
            }}
          >
            {draft.length > 0 ? t("catalog.brandApply", { count: draft.length, products: draftCount }) : t("catalog.brandApplyAll")}
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  )
}
